import { useState, useEffect, useRef } from 'react'
import { Building, Plus, Loader2, X, CheckCircle2 } from 'lucide-react'
import { buildingsService } from '@/services/firebase/buildings'
import type { Building as BuildingType } from '@/services/firebase/buildings'
import toast from 'react-hot-toast'

interface BuildingPickerProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  inputWrapClass?: string
  inputClass?: string
}

const BuildingPicker = ({
  value,
  onChange,
  placeholder = 'e.g. NILA, KEYSTONE, NIAGARABLDG',
  inputWrapClass = 'flex items-center gap-2 border border-gray-200 rounded-2xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 bg-white/90 transition-all shadow-sm',
  inputClass = 'flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent',
}: BuildingPickerProps) => {
  const [buildings, setBuildings] = useState<BuildingType[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    buildingsService.list().then(res => {
      if (res.success && res.data) setBuildings(res.data)
      setLoading(false)
    })
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = buildings.filter(b =>
    !search || b.name.toLowerCase().includes(search.toLowerCase())
  )

  const exactMatch = buildings.some(b => b.name.toLowerCase() === search.trim().toLowerCase())
  const canAddNew = search.trim().length >= 2 && !exactMatch

  const handleSelect = (name: string) => {
    onChange(name)
    setSearch('')
    setOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setSearch('')
  }

  const handleAddNew = async () => {
    if (!canAddNew) return
    setAdding(true)
    const res = await buildingsService.add(search.trim())
    setAdding(false)
    if (res.success && res.data) {
      setBuildings(prev => [...prev, res.data!].sort((a, b) => a.name.localeCompare(b.name)))
      handleSelect(res.data.name)
      toast.success(`Building "${res.data.name}" saved for future use`)
    } else {
      toast.error(res.error ?? 'Failed to save building')
    }
  }

  const handleOpenDropdown = () => {
    setSearch('') // Clear search to show all buildings
    setOpen(true)
  }

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        // Selected state - clickable to change selection
        <div
          className={`${inputWrapClass} cursor-pointer group`}
          onClick={handleOpenDropdown}
        >
          <Building size={14} className="text-blue-500 shrink-0" />
          <span className={`${inputClass} font-semibold text-blue-700`}>{value}</span>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); handleClear() }}
            className="text-gray-400 hover:text-red-500 transition-colors"
          >
            <X size={13} />
          </button>
        </div>
      ) : (
        // Input state
        <div className={inputWrapClass}>
          <Building size={14} className="text-gray-400 shrink-0" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className={inputClass}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              <X size={12} />
            </button>
          )}
          {loading && <Loader2 size={12} className="text-gray-400 animate-spin shrink-0" />}
        </div>
      )}

      {/* Dropdown - show when open, regardless of value (to allow changing selection) */}
      {open && (
        <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden max-h-72 flex flex-col">
          {/* Header */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Building</span>
            <span className="text-[10px] text-gray-400">{filtered.length} found</span>
          </div>

          {/* Search input inside dropdown when value is set */}
          {value && (
            <div className="px-3 py-2 border-b border-gray-100">
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search buildings..."
                className="w-full text-sm outline-none text-gray-900 placeholder-gray-400 bg-gray-50 rounded-lg px-3 py-2"
              />
            </div>
          )}

          {/* Existing buildings */}
          <div className="overflow-y-auto flex-1 divide-y divide-gray-50 max-h-48">
            {filtered.length === 0 && !canAddNew && (
              <p className="py-4 text-center text-xs text-gray-400">No buildings found</p>
            )}
            {filtered.map(b => (
              <button
                key={b.id}
                type="button"
                onClick={() => handleSelect(b.name)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
                  b.name === value ? 'bg-blue-50' : 'hover:bg-blue-50'
                }`}
              >
                <Building size={12} className={`shrink-0 ${b.name === value ? 'text-blue-500' : 'text-gray-400'}`} />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-semibold block ${b.name === value ? 'text-blue-700' : 'text-gray-800'}`}>{b.name}</span>
                  {b.source === 'customer' && (
                    <span className="text-[9px] text-emerald-600">From BSNL Customers</span>
                  )}
                </div>
                {b.name === value && <CheckCircle2 size={12} className="text-blue-500 ml-auto shrink-0" />}
              </button>
            ))}
          </div>

          {/* Add new option */}
          {canAddNew && (
            <button
              type="button"
              onClick={handleAddNew}
              disabled={adding}
              className="flex items-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 border-t border-blue-100 text-blue-700 font-bold text-sm transition-colors disabled:opacity-60 shrink-0"
            >
              {adding ? <Loader2 size={13} className="animate-spin shrink-0" /> : <Plus size={13} className="shrink-0" />}
              Add "{search.trim().toUpperCase()}" as new building
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default BuildingPicker
